export const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const getLogoUrl = (logoUrl, orgId) => {
  if (!logoUrl) return null;
  // If it's a raw S3 key OR a fully qualified S3 bucket URL that we generated previously
  if (logoUrl.includes('uploads/orgs/')) {
    return `${API_URL}/api/organizations/${orgId}/logo`;
  }
  return logoUrl;
};

class ApiClient {
  async request(endpoint, options = {}) {
    const url = `${API_URL}/api${endpoint}`;
    const token = localStorage.getItem('token');

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (options.skipAuth !== true && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle 401 — try token refresh before giving up
    if (response.status === 401 && !options._retried) {
      const refreshed = await this._tryRefresh();
      if (refreshed) {
        // Retry the original request with new token
        return this.request(endpoint, { ...options, _retried: true });
      }
    }

    if (!response.ok) {
      let error = { detail: 'An error occurred' };
      try {
        if (!response.bodyUsed) {
          const text = await response.text();
          if (text) {
            error = JSON.parse(text);
          }
        } else {
          console.warn('Response body already used for', url);
          // Try clone if possible, though unlikely if bodyUsed is true
          // If the body is already used, we might rely on the status text
          if (response.status === 400) {
            // For now, let's assume we can't get the detail
            // But we can check if it's the specific password error by inference if needed
            // or maybe the interceptor attached it to response.data or similar
            if (response._data) error = response._data;
          }
        }
      } catch (e) {
        console.error('Failed to parse error response', e);
      }
      const err = new Error(error.detail || 'An error occurred');
      err.status = response.status;
      throw err;
    }

    return response.json();
  }


  async _tryRefresh() {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return false;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          localStorage.setItem('token', data.token);
          if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
          }
          return true;
        }
      }
      
      // Refresh failed — clear tokens and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return false;
    } catch (e) {
      return false;
    }
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  async getBlob(endpoint) {
    const url = `${API_URL}/api${endpoint}`;
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { method: 'GET', headers });
    // Handle 401 — try token refresh before giving up
    if (response.status === 401 && !options._retried) {
      const refreshed = await this._tryRefresh();
      if (refreshed) {
        // Retry the original request with new token
        return this.request(endpoint, { ...options, _retried: true });
      }
    }

    if (!response.ok) {
      let error = { detail: 'An error occurred' };
      try {
        const text = await response.text();
        if (text) error = JSON.parse(text);
      } catch (e) { /* ignore */ }
      const err = new Error(error.detail || 'An error occurred');
      err.status = response.status;
      err.response = { data: error };
      throw err;
    }
    return response.blob();
  }

  post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    });
  }

  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  async upload(endpoint, file, additionalData = {}) {
    const url = `${API_URL}/api${endpoint}`;
    const token = localStorage.getItem('token');

    const formData = new FormData();
    formData.append('file', file);

    Object.entries(additionalData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  async uploadNoAuth(endpoint, file) {
    const url = `${API_URL}/api${endpoint}`;

    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  // Get URL for audio file
  getAudioUrl(path) {
    return `${API_URL}${path}`;
  }

  /**
   * Upload file via S3 presigned URL (Deal Room).
   * 1. Get presigned URL from backend
   * 2. PUT file directly to S3
   * 3. Register document in DB
   */
  async uploadViaPresigned(file, { dealId, clientId } = {}) {
    const { presignedUrl, fileKey } = await this.post('/documents/presigned-url', {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      deal_id: dealId,
      client_id: clientId,
    });

    const putResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-amz-server-side-encryption': 'AES256',
      },
    });
    if (!putResponse.ok) {
      throw new Error('Failed to upload file to storage');
    }

    return this.post('/documents/register', {
      fileKey,
      deal_id: dealId,
      client_id: clientId,
      original_filename: file.name,
      mime_type: file.type || 'application/octet-stream',
      size: file.size,
    });
  }

  /**
   * Get presigned URL for download/preview. Only for S3-stored documents.
   * For legacy local docs, use getDocumentUrl(storage_path) instead.
   */
  async getDocumentPresignedUrl(docId) {
    const { url } = await this.get(`/documents/${docId}/presigned-url`);
    return url;
  }

  /**
   * Upload signature via S3 presigned URL.
   */
  async uploadSignatureViaPresigned(base64Data, { contractId }) {
    // Convert base64 to Blob
    const fetchResponse = await fetch(base64Data);
    const blob = await fetchResponse.blob();

    const { presignedUrl, fileKey } = await this.post('/signatures/presigned-url', {
      contract_id: contractId,
    });

    const putResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: blob,
      headers: {
        'Content-Type': 'image/png',
        'x-amz-server-side-encryption': 'AES256',
      },
    });

    if (!putResponse.ok) {
      throw new Error('Failed to upload signature to storage');
    }

    return { fileKey };
  }

  /**
   * Get presigned URL for viewing an S3-stored signature.
   */
  async getSignaturePresignedUrl(signatureId) {
    const { url } = await this.get(`/signatures/${signatureId}/presigned-url`);
    return url;
  }
}

const api = new ApiClient();

export const templates = {
  list: () => api.get('/templates'),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

export default api;
